import styled from 'styled-components';
import mediaQuery from 'styles/mediaQuery';

export const MarketingPage = styled.div`
  color: ${({ theme }) => theme.system.neutral.text.primary};
  font-family: inherit;
`;

const SectionBase = styled.section`
  padding: ${({ theme }) =>
    `${theme.spaces.padding['3xl']} ${theme.spaces.padding.lg}`};

  ${mediaQuery.xs} {
    padding: ${({ theme }) => theme.spaces.padding.xxl}
      ${({ theme }) => theme.spaces.padding.lg};
  }
`;

export const WhiteSection = styled(SectionBase)`
  background: ${({ theme }) => theme.system.neutral.surface.primary};
`;

export const BlueSubtleSection = styled(SectionBase)`
  background: ${({ theme }) => theme.system.blue.surface.subtle};
`;

export const BlueSecondarySection = styled(SectionBase)`
  background: ${({ theme }) => theme.system.blue.surface.secondary};
`;

export const SectionInner = styled.div.attrs({ className: 'container' })``;

export const SplitRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 98px;
  align-items: center;

  ${mediaQuery.xs} {
    grid-template-columns: 1fr;
    gap: ${({ theme }) => theme.spaces.gap.xl};
  }
`;

export const SplitImage = styled.img`
  aspect-ratio: 5/4;
  height: 100%;
  width: 100%;
  max-height: 495px;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.large};
`;

export const CopyStack = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spaces.gap.lg};
`;

export const Overline = styled.p<{ $onDark?: boolean }>`
  margin: 0 0 ${({ theme }) => theme.spaces.gap.sm};
  font-size: ${({ theme }) => theme.typography.sizes.xl};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  color: ${({ $onDark, theme }) =>
    $onDark
      ? theme.system.neutral.text.inverted
      : theme.system.neutral.text.primary};

  ${mediaQuery.xs} {
    font-size: ${({ theme }) => theme.typography.sizes.lg};
  }
`;

export const SectionHeading = styled.h2<{ $onDark?: boolean }>`
  margin: 0;
  font-size: ${({ theme }) => theme.typography.sizes['3xl']};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  font-weight: ${({ theme }) => theme.typography.weight.bold};
  letter-spacing: -0.02em;
  color: ${({ $onDark, theme }) =>
    $onDark ? theme.system.neutral.text.inverted : 'inherit'};

  ${mediaQuery.xs} {
    font-size: ${({ theme }) => theme.typography.sizes.xxl};
  }
`;

export const SectionBody = styled.p<{ $onDark?: boolean }>`
  margin: ${({ theme }) => theme.spaces.gap.md} 0 0;
  font-size: ${({ theme }) => theme.typography.sizes.md};
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  color: ${({ $onDark, theme }) =>
    $onDark
      ? theme.system.neutral.text.inverted
      : theme.system.neutral.text.primary};
  /* Apply \n line breaks in the i18n copy. */
  white-space: pre-line;
`;

export const SectionHeader = styled.div`
  margin: 0 0 ${({ theme }) => theme.spaces.gap['3xl']};

  ${mediaQuery.xs} {
    margin-bottom: ${({ theme }) => theme.spaces.padding.xl};
  }
`;

export const CardGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, 1fr);
  gap: ${({ theme }) => theme.spaces.gap.lg};

  ${mediaQuery.xs} {
    grid-template-columns: 1fr;
  }
`;

export const MarketingCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: ${({ theme }) =>
    `${theme.spaces.padding.xxl} ${theme.spaces.padding.xl}`};
  border-radius: ${({ theme }) => theme.radius.large};
  background: ${({ theme }) => theme.system.neutral.surface.primary};
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.08);
  height: 100%;
`;

export const CardIcon = styled.div`
  display: inline-flex;
  margin-bottom: ${({ theme }) => theme.spaces.gap.lg};
  color: ${({ theme }) => theme.system.blue.text.primary};

  svg {
    width: 64px;
    height: 64px;
  }

  ${mediaQuery.xs} {
    svg {
      width: 48px;
      height: 48px;
    }
  }
`;

export const CardTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spaces.gap.lg};
  font-size: ${({ theme }) => theme.typography.sizes.xxl};
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
  font-weight: ${({ theme }) => theme.typography.weight.semibold};
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.system.neutral.text.secondary};

  ${mediaQuery.xs} {
    font-size: ${({ theme }) => theme.typography.sizes.xl};
  }
`;

export const CardDescription = styled.p`
  margin: 0 0 ${({ theme }) => theme.spaces.gap.md};
  font-size: ${({ theme }) => theme.typography.sizes.md};
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  color: ${({ theme }) => theme.system.neutral.text.primary};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const CardBulletList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.spaces.padding.lg};
  display: flex;
  flex-direction: column;
`;

export const CardBullet = styled.li`
  font-size: ${({ theme }) => theme.typography.sizes.md};
  line-height: ${({ theme }) => theme.typography.lineHeight.base};
  color: ${({ theme }) => theme.system.neutral.text.primary};
`;
